import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createSelectionService,
  SelectionStorage,
} from '../services/selectionService';
import { Organization, Store, Device, Venue } from '../types';

// Selection Loading State Enum
export enum SelectionLoadingState {
  INITIALIZING = 'initializing', // Hook is still initializing, don't take any action yet
  LOADING = 'loading', // Actively loading data from services
  READY = 'ready', // Data is loaded and ready to use
}

// Selection State Interface
export interface SelectionHookState<T extends Device = Device> {
  organization: Organization | null;
  store: (Store & { venue?: Pick<Venue, 'id' | 'name'> }) | null;
  device: T | null;
  error: string | null;
  loadingState: SelectionLoadingState; // Keep the loading state for internal use
}

export enum SelectionStep {
  ORGANIZATION = 'organization',
  LOCATIONS = 'locations',
  DEVICE = 'device',
  COMPLETED = 'completed',
}

interface UseSelection<T extends Device = Device>
  extends Pick<
    SelectionHookState<T>,
    'device' | 'organization' | 'store' | 'error'
  > {
  selectOrganization: (org: Organization) => Promise<void>;
  selectStore: (venue: Venue, store: Store) => Promise<void>;
  selectDevice: (device: T) => Promise<void>;
  changeOrganization: () => Promise<void>;
  changeStore: () => Promise<void>;
  changeDevice: () => Promise<void>;
  clearSelections: () => Promise<void>;
  isLoading: boolean;
  step: SelectionStep;
}

/**
 * Hook for managing organization, store, and device selection
 * @param storage - Storage implementation for persisting selections
 */
export const useSelection = <T extends Device = Device>(
  storage: SelectionStorage,
): UseSelection<T> => {
  // Create a selection service instance for this hook
  const selectionService = useMemo(
    () => createSelectionService<T>(storage),
    [storage],
  );

  const [step, setStep] = useState<SelectionStep>(SelectionStep.ORGANIZATION);

  const [state, setState] = useState<SelectionHookState<T>>({
    organization: null,
    store: null,
    device: null,
    loadingState: SelectionLoadingState.INITIALIZING,
    error: null,
  });

  // Determine which step to show based on current selections
  useEffect(() => {
    if (!state.organization) {
      setStep(SelectionStep.ORGANIZATION);
    } else if (!state.store) {
      setStep(SelectionStep.LOCATIONS);
    } else if (!state.device) {
      setStep(SelectionStep.DEVICE);
    }
  }, [state.organization, state.store, state.device]);

  useEffect(() => {
    if (state.organization && state.store && state.device) {
      setStep(SelectionStep.COMPLETED);
    }
  }, [state.organization, state.store, state.device]);

  const loadSelections = useCallback(async () => {
    try {
      setState(prev => ({
        ...prev,
        loadingState: SelectionLoadingState.LOADING,
        error: null,
      }));
      const selections = await selectionService.loadSelections();

      setState({
        organization: selections.organization || null,
        store: selections.store || null,
        device: selections.device || null,
        loadingState: SelectionLoadingState.READY,
        error: null,
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        loadingState: SelectionLoadingState.READY, // Even on error, we're no longer loading
        error:
          error instanceof Error ? error.message : 'Failed to load selections',
      }));
    }
  }, [selectionService]);

  useEffect(() => {
    // Initial load of selections
    loadSelections();

    // Set up listener for selection changes
    const handleSelectionEvent = () => {
      const selectionState = selectionService.getSelectionState();
      // Update the state with the current selection state
      setState({
        organization: selectionState.organization || null,
        store: selectionState.store || null,
        device: selectionState.device || null,
        loadingState: SelectionLoadingState.READY,
        error: null,
      });
    };

    // Single event listener for all selection changes
    selectionService.addEventListener(
      'selection-changed',
      handleSelectionEvent,
    );

    return () => {
      // Clean up selection event listener
      selectionService.removeEventListener(
        'selection-changed',
        handleSelectionEvent,
      );
    };
  }, [loadSelections, selectionService]);

  const selectOrganization = useCallback(
    async (org: Organization) => {
      try {
        setStep(SelectionStep.LOCATIONS);
        setState(prev => ({
          ...prev,
          loadingState: SelectionLoadingState.LOADING,
        }));
        await selectionService.selectOrganization(org);
        // The state will be updated via the event handler
      } catch (error) {
        setState(prev => ({
          ...prev,
          loadingState: SelectionLoadingState.READY,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to select organization',
        }));
      }
    },
    [selectionService],
  );

  const selectStore = useCallback(
    async (venue: Venue, store: Store) => {
      try {
        setStep(SelectionStep.DEVICE);
        setState(prev => ({
          ...prev,
          loadingState: SelectionLoadingState.LOADING,
        }));
        await selectionService.selectStore(venue, store);
        // The state will be updated via the event handler
      } catch (error) {
        setState(prev => ({
          ...prev,
          loadingState: SelectionLoadingState.READY,
          error:
            error instanceof Error ? error.message : 'Failed to select store',
        }));
      }
    },
    [selectionService],
  );

  const selectDevice = useCallback(
    async (device: T) => {
      try {
        setStep(SelectionStep.COMPLETED);
        setState(prev => ({
          ...prev,
          loadingState: SelectionLoadingState.LOADING,
        }));
        // TODO: device pairing mutation
        await selectionService.selectDevice(device);
        // The state will be updated via the event handler
      } catch (error) {
        setState(prev => ({
          ...prev,
          loadingState: SelectionLoadingState.READY,
          error:
            error instanceof Error ? error.message : 'Failed to select device',
        }));
      }
    },
    [selectionService],
  );

  const changeOrganization = useCallback(async () => {
    try {
      // Clear the selections when changing organization
      await selectionService.clearSelections();
      setStep(SelectionStep.ORGANIZATION);
      // The state will be updated via the event handler
    } catch (error) {
      setState(prev => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to change organization',
      }));
    }
  }, [selectionService]);

  const changeStore = useCallback(async () => {
    try {
      // Clear store and device selections when changing store
      await selectionService.clearStoreAndDevice();
      setStep(SelectionStep.LOCATIONS);
      // The state will be updated via the event handler
    } catch (error) {
      setState(prev => ({
        ...prev,
        loadingState: SelectionLoadingState.READY,
        error:
          error instanceof Error ? error.message : 'Failed to change store',
      }));
    }
  }, [selectionService]);

  const changeDevice = useCallback(async () => {
    try {
      setState(prev => ({
        ...prev,
        loadingState: SelectionLoadingState.LOADING,
      }));
      await selectionService.clearDevice();
      setStep(SelectionStep.DEVICE);
    } catch (error) {
      setState(prev => ({
        ...prev,
        loadingState: SelectionLoadingState.READY,
        error:
          error instanceof Error ? error.message : 'Failed to clear device',
      }));
    }
  }, [selectionService]);

  const clearSelections = useCallback(async () => {
    try {
      setState(prev => ({
        ...prev,
        loadingState: SelectionLoadingState.LOADING,
      }));
      await selectionService.clearSelections();
      // The state will be updated via the event handler
    } catch (error) {
      setState(prev => ({
        ...prev,
        loadingState: SelectionLoadingState.READY,
        error:
          error instanceof Error ? error.message : 'Failed to clear selections',
      }));
    }
  }, [selectionService]);

  const isLoading = useMemo(() => {
    return (
      state.loadingState === SelectionLoadingState.LOADING ||
      state.loadingState === SelectionLoadingState.INITIALIZING
    );
  }, [state.loadingState]);

  // Return only isLoading as a property directly, and the rest as methods
  return {
    organization: state.organization,
    store: state.store,
    device: state.device,
    error: state.error,
    selectOrganization,
    selectStore,
    selectDevice,
    changeOrganization,
    changeStore,
    changeDevice,
    clearSelections,
    isLoading,
    step,
  };
};
